import RadiusIcon from '@src/svgs/RadiusIcon';
import { validateNumberInput } from '@src/utility/validation';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';

type Props = {
  action: (propertyMap: Partial<KaraokeConfig>) => void;
  value?: string;
};

const DEFAULT_RADIUS = '0';
const DISPLAY_MAX = 10;
const ACTUAL_MAX = 100;

const convertToDisplayValue = (value: string) => {
  const numValue = parseFloat(value.replace('%', ''));
  return (((numValue || 0) / ACTUAL_MAX) * DISPLAY_MAX).toString();
};

const convertToActualValue = (value: string) => {
  const numValue = parseFloat(value);
  return (((numValue || 0) / DISPLAY_MAX) * ACTUAL_MAX).toString();
};

const TextBackgroundRadius = (props: Props) => {
  const { action, value } = props;

  const _radius = value ? convertToDisplayValue(value) : DEFAULT_RADIUS;
  const [radius, setRadius] = useState(_radius || DEFAULT_RADIUS);
  const isActive = Number(radius) > 0;

  useEffect(() => {
    setRadius(_radius || DEFAULT_RADIUS);
  }, [value]);

  return (
    <Container active={isActive}>
      <RadiusIcon strokeColor={isActive ? 'var(--accent)' : 'var(--border)'} />
      <input
        value={radius}
        type="number"
        step={1}
        min={0}
        max={DISPLAY_MAX}
        onChange={e => {
          const newValue = validateNumberInput(e.target.value, 0, DISPLAY_MAX);
          setRadius(newValue);
        }}
        onBlur={() => {
          action({
            background_border_radius: `${convertToActualValue(radius)}%`,
          });
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            action({
              background_border_radius: `${convertToActualValue(radius)}%`,
            });
          }
        }}
      />
    </Container>
  );
};

export default TextBackgroundRadius;

const Container = styled.div<{ active: boolean }>`
  height: 40px;
  border: 1px solid
    ${({ active }) => (active ? 'var(--accent)' : 'var(--border)')};
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px 0 8px;
  cursor: pointer;

  /*
   * Bordered-field-primitive hover/focus standard — see
   * docs/design-system.md "Form Fields (Bordered Primitives)".
   * Active state already uses accent; hover only escalates the
   * inactive border to --text-muted. Focus-within catches keyboard
   * focus on the inner <input>.
   */
  transition: border-color var(--transition-fast);
  &:hover {
    border-color: ${({ active }) =>
      active ? 'var(--accent)' : 'var(--text-muted)'};
  }
  &:focus-within {
    border-color: var(--accent);
  }

  input {
    width: 24px;
    background: none;
    border: none;
    outline: none;
    color: var(--text-secondary);
    font-size: 14px;
    padding: 0;
    cursor: pointer;

    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
      display: none;
    }
  }
`;
